/**
 * Relation layout algorithm for the Needs Map
 * Implements a more stable and predictable force-directed layout
 */

import { Node, Edge } from '@xyflow/react';
import { TopicNode } from '@/types/nodeTypes';
import { TopicEdge } from '@/types/edgeTypes';

/**
 * Apply relation layout to nodes
 * Uses a completely redesigned force-directed algorithm for more balanced layouts
 */
export function applyRelationLayout(
  nodes: Node[],
  edges: Edge[],
  width: number = 1000,
  height: number = 600,
  iterations: number = 150, // Increased iterations for more stable convergence
  defaultNodeWidth: number = 250,
  defaultNodeHeight: number = 150
): Node[] {
  if (nodes.length === 0) return [];
  
  // Create map for quick edge lookup
  const edgeMap = new Map<string, boolean>();
  edges.forEach(edge => {
    edgeMap.set(`${edge.source}-${edge.target}`, true);
    edgeMap.set(`${edge.target}-${edge.source}`, true);
  });
  
  // Create strength map for edges
  const strengthMap = new Map<string, number>();
  edges.forEach(edge => {
    const strength = (edge.data?.strength as string) || 'medium';
    let value = 0.5; // Default medium
    
    if (strength === 'strong') value = 0.8;
    else if (strength === 'weak') value = 0.3;
    
    strengthMap.set(`${edge.source}-${edge.target}`, value);
    strengthMap.set(`${edge.target}-${edge.source}`, value);
  });
  
  // Create initial positions using a structured approach
  // Start with a circular layout to avoid initial clumping
  const initialPositions = nodes.map((node, i) => {
    // Calculate position on a circle
    const angle = (2 * Math.PI * i) / Math.max(1, nodes.length);
    const radius = Math.min(width, height) * 0.35;
    
    return {
      id: node.id,
      x: width / 2 + radius * Math.cos(angle),
      y: height / 2 + radius * Math.sin(angle),
      vx: 0,
      vy: 0,
      fixed: false, // Whether position is fixed
      importance: ((node.data?.importance as number) || 3)
    };
  });
  
  // Fix important nodes in structured positions to serve as anchors
  const centralNodes = initialPositions
    .filter(p => p.importance >= 4)
    .slice(0, 1); // Use the most important node as center
  
  if (centralNodes.length > 0) {
    centralNodes[0].fixed = true;
    centralNodes[0].x = width / 2;
    centralNodes[0].y = height / 2;
  }
  
  // Layout parameters - carefully tuned
  const params = {
    // Forces
    repulsionStrength: 800,       // Base repulsion between nodes
    springStiffness: 0.08,        // Edge spring stiffness
    springLength: 250,            // Preferred edge length
    gravity: 0.03,                // Global gravity toward center
    damping: 0.7,                 // Velocity damping factor
    // Distances
    minDistance: 300,             // Minimum distance between nodes
    maxApplyDistance: 500,        // Maximum distance for force application
    // Other
    timeStep: 0.6,                // Integration time step
    coolingFactor: 0.98,          // Rate at which system cools down
  };
  
  // Main force-directed layout loop
  let energy = 1.0;
  for (let iter = 0; iter < iterations && energy > 0.001; iter++) {
    // Reset forces
    initialPositions.forEach(pos => {
      pos.vx = 0;
      pos.vy = 0;
    });
    
    // 1. Apply repulsive forces between all nodes
    for (let i = 0; i < initialPositions.length; i++) {
      const nodeA = initialPositions[i];
      
      for (let j = i + 1; j < initialPositions.length; j++) {
        const nodeB = initialPositions[j];
        
        const dx = nodeB.x - nodeA.x;
        const dy = nodeB.y - nodeA.y;
        const distanceSquared = dx * dx + dy * dy;
        const distance = Math.sqrt(distanceSquared) || 1;
        
        // Calculate repulsive force (inverse square law)
        // Stronger repulsion at close distances
        if (distance < params.maxApplyDistance) {
          // Add extra strength when nodes are too close
          let repulsionMultiplier = 1.0;
          if (distance < params.minDistance) {
            repulsionMultiplier = 2.0 * params.minDistance / distance;
          }
          
          const force = params.repulsionStrength * repulsionMultiplier / distanceSquared;
          const forceX = (dx / distance) * force;
          const forceY = (dy / distance) * force;
          
      // Apply forces to both nodes (if not fixed)
      if (!nodeA.fixed) {
        nodeA.vx -= forceX;
        nodeA.vy -= forceY;
      }
      
      if (!nodeB.fixed) {
        nodeB.vx += forceX;
        nodeB.vy += forceY;
      }
        }
      }
    }
    
    // 2. Apply attractive forces between connected nodes
    edges.forEach(edge => {
      const sourcePos = initialPositions.find(p => p.id === edge.source);
      const targetPos = initialPositions.find(p => p.id === edge.target);
      
      if (sourcePos && targetPos) {
        const dx = targetPos.x - sourcePos.x;
        const dy = targetPos.y - sourcePos.y;
        const distance = Math.sqrt(dx * dx + dy * dy) || 1;
        
        // Get edge strength
        const strengthKey = `${edge.source}-${edge.target}`;
        const edgeStrength = strengthMap.get(strengthKey) || 0.5;
        
        // Hooke's law: F = k * (distance - rest_length)
        const displacement = distance - params.springLength;
        const force = params.springStiffness * displacement * edgeStrength;
        
        const forceX = (dx / distance) * force;
        const forceY = (dy / distance) * force;
        
        // Apply forces to both nodes (if not fixed)
        if (!sourcePos.fixed) {
          sourcePos.vx += forceX;
          sourcePos.vy += forceY;
        }
        
        if (!targetPos.fixed) {
          targetPos.vx -= forceX;
          targetPos.vy -= forceY;
        }
      }
    });
    
    // 3. Apply gravity force toward center
    initialPositions.forEach(pos => {
      if (pos.fixed) return; // Skip fixed positions
      
      const dx = width / 2 - pos.x;
      const dy = height / 2 - pos.y;
      const distance = Math.sqrt(dx * dx + dy * dy) || 1;
      
      pos.vx += (dx / distance) * params.gravity * distance;
      pos.vy += (dy / distance) * params.gravity * distance;
    });
    
    // 4. Update positions
    let totalMovement = 0;
    
    initialPositions.forEach(pos => {
      if (pos.fixed) {
        // Keep fixed position
        return;
      }
      
      // Apply damping
      pos.vx *= params.damping;
      pos.vy *= params.damping;
      
      // Limit maximum velocity to avoid instability
      const speed = Math.sqrt(pos.vx * pos.vx + pos.vy * pos.vy);
      if (speed > 10) {
        pos.vx = (pos.vx / speed) * 10;
        pos.vy = (pos.vy / speed) * 10;
      }
      
      // Update position
      const prevX = pos.x;
      const prevY = pos.y;
      
      pos.x += pos.vx * params.timeStep;
      pos.y += pos.vy * params.timeStep;
      
      // Calculate total movement for convergence check
      const dx = pos.x - prevX;
      const dy = pos.y - prevY;
      totalMovement += Math.sqrt(dx * dx + dy * dy);
    });
    
    // Update energy level based on total movement
    energy = totalMovement / initialPositions.length;
    
    // Cool down the system
    params.repulsionStrength *= params.coolingFactor;
  }
  
  // 5. Prevent nodes from going outside boundaries with margins
  const margin = defaultNodeWidth / 2 + 20;
  
  initialPositions.forEach(pos => {
    pos.x = Math.max(margin, Math.min(width - margin, pos.x));
    pos.y = Math.max(margin, Math.min(height - margin, pos.y));
  });
  
  // 6. Detect overlaps and adjust positions if necessary
  const minSeparation = defaultNodeWidth * 0.9;
  let hasOverlap = true;
  let overlapIters = 0;
  
  while (hasOverlap && overlapIters < 20) {
    hasOverlap = false;
    overlapIters++;
    
    // Check and resolve overlaps
    for (let i = 0; i < initialPositions.length; i++) {
      const nodeA = initialPositions[i];
      
      for (let j = i + 1; j < initialPositions.length; j++) {
        const nodeB = initialPositions[j];
        
        const dx = nodeB.x - nodeA.x;
        const dy = nodeB.y - nodeA.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < minSeparation) {
          hasOverlap = true;
          
          // Move nodes apart
          const moveX = (dx / distance) * ((minSeparation - distance) / 2 + 1);
          const moveY = (dy / distance) * ((minSeparation - distance) / 2 + 1);
          
          if (!nodeA.fixed) {
            nodeA.x -= moveX;
            nodeA.y -= moveY;
          }
          
          if (!nodeB.fixed) {
            nodeB.x += moveX;
            nodeB.y += moveY;
          }
          
          // Ensure nodes stay within bounds
          nodeA.x = Math.max(margin, Math.min(width - margin, nodeA.x));
          nodeA.y = Math.max(margin, Math.min(height - margin, nodeA.y));
          nodeB.x = Math.max(margin, Math.min(width - margin, nodeB.x));
          nodeB.y = Math.max(margin, Math.min(height - margin, nodeB.y));
        }
      }
    }
  }

  // 7. Ensure the layout fits within the view with some scaling if needed
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  
  initialPositions.forEach(pos => {
    minX = Math.min(minX, pos.x);
    minY = Math.min(minY, pos.y);
    maxX = Math.max(maxX, pos.x);
    maxY = Math.max(maxY, pos.y);
  });
  
  const boundWidth = maxX - minX + defaultNodeWidth;
  const boundHeight = maxY - minY + defaultNodeHeight;
  const scaleX = width * 0.85 / boundWidth;
  const scaleY = height * 0.85 / boundHeight;
  const scale = Math.min(scaleX, scaleY, 1); // Only scale down, never up
  
  const offsetX = (width - boundWidth * scale) / 2;
  const offsetY = (height - boundHeight * scale) / 2;
  
  // Create final nodes with positions
  return nodes.map(node => {
    const position = initialPositions.find(p => p.id === node.id);
    const finalX = position ? (position.x - minX) * scale + offsetX : width / 2;
    const finalY = position ? (position.y - minY) * scale + offsetY : height / 2;
    
    return {
      ...node,
      position: { x: finalX, y: finalY },
      data: {
        ...node.data,
        width: defaultNodeWidth,
        height: defaultNodeHeight
      }
    };
  });
}

/**
 * Apply relation layout to topic nodes
 */
export function applyRelationLayoutToTopicNodes(
  nodes: TopicNode[],
  edges: TopicEdge[],
  width: number = 1000,
  height: number = 600,
  defaultNodeWidth: number = 250,
  defaultNodeHeight: number = 150
): TopicNode[] {
  if (nodes.length === 0) return [];

  // Convert to ReactFlow nodes and edges for processing
  const rfNodes = nodes.map(node => ({
    id: node.id,
    position: node.position || {
      x: width / 2 + (Math.random() - 0.5) * width * 0.4,
      y: height / 2 + (Math.random() - 0.5) * height * 0.4
    },
    data: {
      ...node,
      // Always set these properties to ensure consistent rendering
      width: defaultNodeWidth,
      height: defaultNodeHeight
    }
  }));
  
  const rfEdges = edges.map(edge => ({
    id: edge.id,
    source: edge.source,
    target: edge.target,
    data: {
      strength: edge.strength
    }
  }));

  // Apply layout with increased iterations for better spacing
  const positionedNodes = applyRelationLayout(rfNodes, rfEdges, width, height, 150, defaultNodeWidth, defaultNodeHeight);

  // Map positions back to topic nodes
  return nodes.map(node => {
    const positionedNode = positionedNodes.find(n => n.id === node.id);
    
    return {
      ...node,
      position: positionedNode?.position || node.position,
      // Always use default values for width and height
      width: defaultNodeWidth,
      height: defaultNodeHeight
    };
  });
}